import { auth } from '@/firebase';
import { ReconnectingEventSource } from '@/api/ReconnectingEventSource';

const URL = 'https://api.meshcommengineering.com/api/v1/';

const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const pickOptionsDetails = (payload) => {
    const candidates = [
        payload?.body?.details?.details,
        payload?.body?.details?.options,
        payload?.body?.details,
        payload?.details?.details,
        payload?.details,
        payload?.body?.options,
        payload?.options,
    ];

    for (const candidate of candidates) {
        if (!isPlainObject(candidate)) continue;
        if (isPlainObject(candidate.details)) {
            const nested = candidate.details;
            const nestedLooksLikeSourceMap = Object.values(nested).some((v) => isPlainObject(v));
            if (nestedLooksLikeSourceMap) return nested;
        }
        // Best case: source map (keys => field-type map objects)
        const hasSourceMapShape = Object.values(candidate).some((v) => isPlainObject(v));
        if (hasSourceMapShape) return candidate;
    }
    return isPlainObject(payload?.body?.details) ? payload.body.details : {};
};

const pickOptionsStatus = (response, payload) => {
    return (
        payload?.body?.status ??
        payload?.status ??
        (payload?.statusCode === 200 ? 'success' : null) ??
        (response.ok ? 'success' : 'error')
    );
};

class Data {
    static async get(dataID, token=null, { signal } = {}) {
        const authToken = token || await auth.currentUser.getIdToken();

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);
        const mergedSignal = signal
            ? AbortSignal.any([signal, controller.signal])
            : controller.signal;

        let response;
        try {
            response = await fetch(`${URL}data/options/${dataID}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                signal: mergedSignal,
            });
        } finally {
            clearTimeout(timeoutId);
        }
        const payload = await response.json();
        const details = pickOptionsDetails(payload);
        const status = pickOptionsStatus(response, payload);

        // Backward-compatible shape for all callers:
        // - some expect response.body.status/response.body.details
        // - others expect response.details
        return {
            ...payload,
            details,
            body: {
                ...(isPlainObject(payload?.body) ? payload.body : {}),
                status,
                details,
            },
        };
    }

    static async post(body, token=null) {
        const authToken = token || await auth.currentUser.getIdToken();
        
        const response = await fetch(`${URL}data/`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        });

        const responseData = await response.json();
        if (responseData.statusCode === 200) {
            let data = responseData.body.details.data;
            let pagination = responseData.body.details.pagination;
            while (pagination.hasNext) {
                const nextResponse = await fetch(`${URL}data/`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(pagination)
                });
                const nextResponseData = await nextResponse.json();
                data = data.concat(nextResponseData.body.details.data);
                pagination = nextResponseData.body.details.pagination;
            }
            return data;
        }
        else {
            if (responseData.statusCode === 404 && responseData.body?.status === 'Source Not Found') {
                const err = new Error(responseData.body?.message || 'Source not found');
                err.isSourceNotFound = true;
                err.sourceName = responseData.body?.details?.source || body.subject;
                throw err;
            }
            const message =
                responseData.detail ??
                responseData.message ??
                (typeof responseData.body?.details === 'string'
                    ? responseData.body.details
                    : JSON.stringify(responseData));
            throw new Error(message);
        }
    }

    static async stream(dataID, topic, token=null) {
        const url = `${URL}stream/data/${encodeURIComponent(dataID)}/${encodeURIComponent(topic)}`;

        // Refresh the Firebase ID token on every (re)connect so long-lived
        // streams don't die with 401s after the original token expires
        // (~1 hour). Callers that supply an explicit token (e.g. Web Workers
        // that can't touch `auth`) get the static token reused verbatim.
        const getHeaders = async () => {
            const authToken = token || await auth.currentUser.getIdToken();
            return { Authorization: `Bearer ${authToken}` };
        };

        return new ReconnectingEventSource(url, { getHeaders });
    }
}

export default Data;